import createServerInstance from "../server";
import { QueryFnParams } from "@app/utils/routesTypes";
import { useQueryWithContext } from "@app/utils/reactQuery";
import { RouteToBusinessLine } from "constants/base";
import {
  useFuturesUrlParams,
  useSpotUrlParams,
  StrategyPath,
  useStrategyUrlParams,
  useUrlParams,
} from "constants/routesPathEnum";
import {
  MixContractTickerInfo,
  MixContractPlaceInfo,
  MixSpotTickerInfo,
} from "store/entity";
import { LoadData } from "@app/utils/routesTypes";
import { BusinessLineEnum } from "constants/strategy";
import { useLocation, useParams } from "react-router-dom";

const serverInstance = createServerInstance("");

export class TickerServices {
  static getFuturesTicker({ params }: QueryFnParams) {
    let symbolId = params.symbolId;
    let businessLine = RouteToBusinessLine[params.businessLine];
    let param =
      symbolId.indexOf("_") > 0
        ? {
            symbolId,
            businessLine,
          }
        : {
            symbolCodeDisplayName: symbolId,
            businessLine,
          };
    return serverInstance.post("/v1/mcp/market/ticker", param);
  }

  static getStrategyFuturesTicker({ params }: QueryFnParams) {
    let symbolId = params.symbolId;
    let businessLine = RouteToBusinessLine["future-strategy"];
    let param =
      symbolId.indexOf("_") > 0
        ? {
            symbolId,
            businessLine,
          }
        : {
            symbolCodeDisplayName: symbolId,
            businessLine,
          };
    return serverInstance.post("/v1/mcp/market/ticker", param);
  }

  static getFuturesPlaceConfig({ params }: QueryFnParams) {
    return serverInstance.get("/v1/mcp/ct/place", {
      symbolId: params.symbolId,
    });
  }

  static getSpotTicker({ params }: QueryFnParams) {
    // 迁移过来的代码请求的接口 /v1/spot/public/getTickerInfoBySymbolCodeDisplayName
    return serverInstance
      .get("/v1/spot/public/getTickerInfo", {
        symbolId: params.symbolId.replace("-", "").toLocaleUpperCase() + '_' + StrategyPath.spot,
      })
      .then((res) => {
        if (res && res[0]) {
          let target = res[0];
          target.baseSymbol = target?.baseSymbolDisplayName;
          target.pricedSymbol = target?.pricedSymbolDisplayName;
        }
        return res;
      });
  }

  static getStrategySpotTicker({ params }: QueryFnParams) {
    // 迁移过来的代码请求的接口 /v1/spot/public/getTickerInfoBySymbolCodeDisplayName
    return serverInstance
      .get("/v1/spot/public/getTickerInfo", {
        symbolId: params.symbolId.replace("-", "").toLocaleUpperCase() + '_' + StrategyPath.spot,
      })
      .then((res) => {
        if (res && res[0]) {
          let target = res[0];
          target.baseSymbol = target?.baseSymbolDisplayName;
          target.pricedSymbol = target?.pricedSymbolDisplayName;
        }
        return res;
      });
  }
}

export const futuresTicker: LoadData = {
  queryKey: ["futures-ticker"],
  queryFn: TickerServices.getFuturesTicker,
  withoutPathname: true,
};

export const getStrategyFuturesTicker: LoadData = {
  queryKey: ["strategy-futures-ticker"],
  queryFn: TickerServices.getStrategyFuturesTicker,
  withoutPathname: false,
};

export const futuresPlaceConfig: LoadData = {
  queryKey: ["current-futures-place-config"],
  queryFn: TickerServices.getFuturesPlaceConfig,
};

export const spotTicker: LoadData = {
  queryKey: ["spot-ticker"],
  queryFn: TickerServices.getSpotTicker,
  withoutPathname: true,
};

export const getStrategySpotTicker: LoadData = {
  queryKey: ["strategy-spot-ticker"],
  queryFn: TickerServices.getStrategySpotTicker,
  withoutPathname: false,
};
export const useFuturesTickerQuery = () => {
  const { symbolId, businessLine } = useFuturesUrlParams();

  return useQueryWithContext<
    unknown,
    null,
    MixContractTickerInfo & { chgUTC: string }
  >(
    {
      ...futuresTicker,
      queryFn: () =>
        TickerServices.getFuturesTicker({
          params: {
            symbolId,
            businessLine,
          },
        }),
    },
    futuresTicker.withoutPathname
  );
};
export const useStrategyFuturesTickerQuery = () => {
  const { symbolId } = useParams();
  const { pathname } = useLocation();
  const isSpot = pathname.includes("/spot");

  return useQueryWithContext<
    unknown,
    null,
    MixContractTickerInfo & { chgUTC: string }
  >(
    {
      ...getStrategyFuturesTicker,
      queryFn: () => {
        if (isSpot) {
          return null;
        }
        TickerServices.getStrategyFuturesTicker({
          params: {
            symbolId,
          },
        });
      },
    },
    getStrategyFuturesTicker.withoutPathname
  );
};

export const useFuturesPlaceConfigQuery = () => {
  const { symbolId } = useFuturesUrlParams();

  return useQueryWithContext<unknown, null, MixContractPlaceInfo>({
    ...futuresPlaceConfig,
    queryFn: () =>
      TickerServices.getFuturesPlaceConfig({
        params: {
          symbolId,
        },
      }),
  });
};

export const useSpotTickerQuery = () => {
  const { symbolId } = useSpotUrlParams();

  return useQueryWithContext<unknown, null, MixSpotTickerInfo>(
    {
      ...spotTicker,
      queryFn: () =>
        TickerServices.getSpotTicker({
          params: {
            symbolId,
          },
        }),
    },
    spotTicker.withoutPathname
  );
};

export const useStrategySpotTickerQuery = () => {
  const { symbolId } = useParams();
  const { pathname } = useLocation();
  const isSpot = pathname.includes("/spot");

  return useQueryWithContext<unknown, null, MixSpotTickerInfo>(
    {
      ...getStrategySpotTicker,
      queryFn: () => {
        if (!isSpot) {
          return null;
        }
        TickerServices.getStrategySpotTicker({
          params: {
            symbolId,
          },
        });
      },
    },
    getStrategySpotTicker.withoutPathname
  );
};
